/**
 * #4533: keeping a newly-connected client off the paywall while the server makes up its mind.
 *
 * ## What the "bridge" is
 *
 * When someone opens their coach's invitation link, the app claims the invite and then RESTARTS
 * itself, because almost everything the app decides at start-up depends on whether the person is
 * a paying user, and they only became one a moment ago.
 *
 * The restart is the problem. The app learns whether someone has paid access from the roles list
 * that comes back with the login call, and immediately after a claim that list often has not
 * caught up yet. A restart at that moment would read "no access" and drop a brand-new client on
 * the Subscribe paywall — the worst possible first screen for someone whose coach has just paid
 * for them.
 *
 * So the claim flow leaves a note for itself before restarting (a `sessionStorage` marker), which
 * survives the restart and means: hold this person on the Coach Claim page and keep asking the
 * server, do not route them anywhere yet. That marker is the bridge.
 *
 * ## What went wrong
 *
 * The marker was thrown away as soon as ONE login answered "paid" — and the very next thing that
 * happened was another restart, which asked again and could get a different answer.
 *
 * It really can. When this was written, the server copied the roles list out of CouchDB's sign-in
 * answer, which CouchDB serves through a cache that is not shared across the cluster — so two
 * logins a second apart disagreed. On the CI run
 * that exposed this, one client's
 * eight consecutive logins answered: none, none, none, paid, none, none, paid, none. The flow saw
 * the seventh, dropped its marker, restarted, got the eighth, and landed on the paywall.
 *
 * That cause was removed on 2026-08-15 (shipped in v1.32.0): the server now builds the list from
 * the stored user record, a plain document read the cache has no part in, so a stale cache can no
 * longer reach it. cybsol/mh-physique#4552 was closed on that.
 *
 * **This bridge is still needed.** A narrower window survives by a different route — a CouchDB
 * write that reached fewer replicas than the write quorum wanted still reports success, so a
 * later read of that record can be stale, and a record stale enough to predate the claim has no
 * coach grant on it for the server to report. See design-docs/coach-mode.md, "Carrying the bridge
 * across the reload", for what is recorded about it.
 *
 * This did not use to show, because an empty roles list used to fall back to the role remembered
 * from the previous session, which the claim had just set to paid. That fallback was removed on
 * purpose (it was letting revoked users back in), which left this flow with nothing holding it up.
 *
 * ## What this module decides
 *
 * The fix is to carry the marker ACROSS the restart instead of dropping it before one. A restart
 * that reads a good answer clears the marker itself and routes the person onward, so carrying it
 * costs nothing in the normal case.
 *
 * What it does need is a limit. Without one, a server that never stops giving stale answers would
 * restart the app forever, which is a worse first screen than the paywall. So the marker may be
 * re-armed only a few times, and after that the flow gives up and lets the person be routed by
 * whatever the server last said.
 *
 * Pure on purpose (no Angular, no `sessionStorage`), so it can be unit-tested under plain Node.
 * Same shape and same reason as `coach-claim-failure-policy.ts` next to it.
 */

/**
 * How many times the bridge may be carried across a restart.
 *
 * Each re-arm buys one more restart plus one more round of polling. Three is the total number of
 * start-ups the person can sit through: the one right after the claim, plus these two. Past that we
 * are no longer waiting out a slow cache — something is properly wrong, and repeatedly restarting
 * the app in front of someone is not a way to find out what.
 */
export const MAX_CLAIM_BRIDGE_REARMS = 2;

/** What to do about the bridge marker when the claim flow is about to restart the app. */
export interface ClaimBridgeReloadDecision {
  /** Whether to put the marker back, so the restart still holds this person on the Coach Claim page. */
  shouldRearm: boolean;
  /** The re-arm count to store. Unchanged on a refusal, so a refusal cannot creep the count upward. */
  nextRearmCount: number;
}

/**
 * Read the stored re-arm count.
 *
 * Anything that is not a whole number at or above zero counts as zero. `sessionStorage` hands back
 * strings, and another tab, an extension, or an older version of this code could have written
 * something this module never would. The direction that has to be ruled out is a value BELOW zero,
 * or one that is not a number at all: both compare false against the limit, so they would allow
 * re-arms without end — the restart loop the limit exists to stop.
 *
 * A value at or above the limit is left alone rather than reset, because refusing is the safe
 * answer to it: the person is routed by whatever the server last said, which is where they would
 * have ended up anyway. Only this module ever writes the key, and it only ever writes 1 or 2.
 */
export function parseClaimBridgeRearmCount(raw: string | null | undefined): number {
  if (raw === null || raw === undefined) return 0;
  return sanitizeRearmCount(Number(raw));
}

/** The same "anything unrecognisable means zero" rule, for a count we already hold as a number. */
function sanitizeRearmCount(value: number): number {
  if (!Number.isInteger(value) || value < 0) return 0;
  return value;
}

/**
 * Decide whether this restart may carry the bridge marker onward.
 *
 * Takes the count rather than reading storage itself, so the rule can be tested without a browser.
 */
export function decideClaimBridgeReload(rearmsSoFar: number): ClaimBridgeReloadDecision {
  const used = sanitizeRearmCount(rearmsSoFar);
  if (used >= MAX_CLAIM_BRIDGE_REARMS) {
    return { shouldRearm: false, nextRearmCount: used };
  }
  return { shouldRearm: true, nextRearmCount: used + 1 };
}
